/**
 * @file PublishPagesSection.tsx
 * @description Раздел «Страницы» окна «Опубликовать» (phase19.16 Р3а;
 * uiux-r3 §3.2; SB102, SB105, SB126–SB128): пункты страниц и мест с правками
 * и скрытых с версией на сайте, ошибки стоп-листа отмеченных страниц с
 * «Исправить →» — ссылкой на блок в конструкторе (окно живёт в каркасе вида
 * сайта, вне контекста конструктора), галочка SB125 и отказы сервера
 * (кто изменил черновик, занятый адрес) с кнопкой «Обновить».
 *
 * Р3в (SB127, SB132): под отмеченной страницей — SEO-предупреждения её
 * черновика (`usePageSeoIssues`, публикацию не блокируют).
 *
 * phase19.28 этап А: под отмеченной страницей — предупреждение «На странице
 * остался текст шаблона: …» (подсказки [З] стартового сайта; публиковать
 * можно, решение пользователя 2026-10-24).
 */

import { AlertCircle, RefreshCw } from "lucide-react";
import { Link } from "react-router";

import { Button } from "../../ui/button";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { SectionLabel } from "../../ui/SectionLabel";
import { useSiteView } from "../site-view-context";
import { PublishAcknowledgeRow } from "./PublishAcknowledgeRow";
import { PublishPageItem } from "./PublishPageItem";
import { usePageSeoIssues } from "./usePageSeoIssues";
import { useStarterHints } from "./useStarterHints";
import type { PagesPublishState } from "./usePagesPublish";
import { PAGE_DRAFT_SAVED_TEXT } from "../../../lib/site-builder/publish-rules";
import { starterHintsText } from "../../../lib/site-builder/starter-hints";
import { useBuilderLink } from "../../../hooks/site-builder/useBuilderLink";

interface Props {
  pages: PagesPublishState;
  acknowledge: { checked: boolean; onChange: (value: boolean) => void; allowed: boolean };
}

export function PublishPagesSection({ pages, acknowledge }: Props) {
  const view = useSiteView();
  const link = useBuilderLink();
  const hintsOf = useStarterHints(pages.rows, pages.selection);
  const seoOf = usePageSeoIssues(pages.rows, pages.selection);
  if (!pages.canView) return null;
  if (pages.loading) return <p className="type-body text-on-surface-variant" aria-busy="true">Проверяем страницы…</p>;
  if (pages.rows.length === 0 && !pages.loadError && !pages.problem) return null;

  return (
    <div className="flex flex-col gap-2">
      <SectionLabel as="h2">Страницы</SectionLabel>
      {pages.loadError ? <NoticeStrip tone="warning">{pages.loadError}</NoticeStrip> : null}
      {pages.problem ? (
        <NoticeStrip
          tone="warning"
          action={
            <Button variant="outline" onClick={() => void pages.refresh()}>
              <RefreshCw aria-hidden />
              Обновить
            </Button>
          }
        >
          {pages.problem}
        </NoticeStrip>
      ) : null}
      {pages.canPublish ? null : <p className="type-caption text-on-surface-variant">{PAGE_DRAFT_SAVED_TEXT}.</p>}
      {pages.rows.map((row) => {
        const hints = starterHintsText(hintsOf(row));
        const seo = seoOf(row);
        return (
          <div key={row.pageId} className="flex flex-col gap-1">
            <PublishPageItem
              row={row}
              value={pages.selection[row.pageId] ?? { checked: false, show: false }}
              disabled={!pages.canPublish}
              onChange={(patch) => pages.select(row.pageId, patch)}
            />
            {hints ? (
              <NoticeStrip tone="warning" className="ml-7">
                <span className="sr-only">Предупреждение, публиковать можно: </span>
                {hints}
              </NoticeStrip>
            ) : null}
            {seo.length > 0 ? (
              <NoticeStrip tone="warning" className="ml-7" title="Для поиска (публиковать можно)">
                {seo.join("; ")}.
              </NoticeStrip>
            ) : null}
          </div>
        );
      })}
      {pages.errors.length > 0 ? (
        <>
          <SectionLabel as="h2">Проверки страниц</SectionLabel>
          <ul className="flex flex-col gap-2">
            {pages.errors.map((check) => (
              <li key={`${check.pageId}:${check.sectionKey}:${check.path}:${check.term}`} className="flex items-start gap-2">
                <AlertCircle aria-hidden className="mt-0.5 size-4 shrink-0 text-destructive" />
                <span className="sr-only">Мешает публикации: </span>
                <span className="type-body min-w-0 flex-1 text-on-surface">
                  {check.message}
                  <Link
                    to={link({ pageId: check.pageId, screen: null, panel: null, sel: { kind: "block", id: check.sectionKey } })}
                    onClick={() => view.openDialog(null)}
                    className="flex min-h-12 items-center font-medium text-primary hover:underline lg:inline-flex lg:min-h-0 lg:pl-1"
                  >
                    Исправить →
                  </Link>
                </span>
              </li>
            ))}
          </ul>
          <PublishAcknowledgeRow
            errors={pages.errors.map((c) => ({ code: c.code, level: c.level, message: c.message }))}
            checked={acknowledge.checked}
            onChange={acknowledge.onChange}
            allowed={acknowledge.allowed}
          />
        </>
      ) : null}
    </div>
  );
}
